/**
 * The client-side color ladder (HW-12340 addendum design doc, "Color resolution -- client-side,
 * not persisted" section). Five steps, most specific first. Every step fires only for what is
 * actually SELECTED on the calling form right now -- never "does this crop have a color
 * somewhere in its purpose list." Pass `null`/`undefined` for any step with nothing selected (or
 * no color at that step); the ladder falls straight through to the next step, it never searches
 * for a substitute elsewhere in the data.
 *
 * 1. `override` -- an existing manual Colour Override color for this exact (crop, purpose),
 *    already resolved by the caller (`ai360.user`'s color-override, unchanged by this ladder).
 * 2. `chemistry` -- the currently selected chemistry's Agvance color, if any.
 * 3. `purpose` -- the currently selected purpose's Agvance color, if any.
 * 4. `crop` -- the currently selected crop's Agvance-level color, if any.
 * 5. `defaultMapColor` -- the AgBytes floor value. Always required, never itself overwritten.
 *
 * Framework-free by design (no React, no `@ai360/core` import) so both this phase's form and
 * Phase 8's Colour Override screen can call it identically. Every color input is duck-typed
 * ({red,green,blue}-shaped) so callers pass `AgvanceAPI.IAgvColor`/`AgBytesAPI.ICropLinkedPurpose`
 * -style objects straight through, no adapter step.
 */

export interface IAgvColorLike {
    red: number;
    green: number;
    blue: number;
}

export interface IResolveCropColorSource {
    color?: IAgvColorLike | null;
}

export interface IResolveCropColorInput {
    /** A resolved Colour Override hex value for this (crop, purpose), or null/undefined if none. */
    override?: string | null;
    /** The currently SELECTED chemistry, or null/undefined if none is selected. */
    chemistry?: IResolveCropColorSource | null;
    /** The currently SELECTED purpose, or null/undefined if none is selected. */
    purpose?: IResolveCropColorSource | null;
    /** The currently SELECTED crop, or null/undefined if none is selected. */
    crop?: IResolveCropColorSource | null;
    /** AgBytes's own floor color. Required -- the ladder always has somewhere to land. */
    defaultMapColor: string;
}

const toHexChannel = (value: number): string =>
    Math.max(0, Math.min(255, Math.round(value)))
        .toString(16)
        .padStart(2, "0")
        .toUpperCase();

/**
 * Converts an Agvance RGB color to a 6-digit hex string ("#RRGGBB"), dropping alpha -- every
 * consumer of the ladder's output (`ColorPicker`, swatches, comparisons against `defaultMapColor`)
 * expects a plain hex string, never an rgba() value. Returns `null` for a missing color so it
 * composes directly with `||` in `resolveCropColor`.
 */
export const agvColorToHex = (color: IAgvColorLike | null | undefined): string | null =>
    color == null
        ? null
        : `#${toHexChannel(color.red)}${toHexChannel(color.green)}${toHexChannel(color.blue)}`;

/**
 * Resolves the display color for a (crop, purpose, chemistry) selection per the five-step
 * ladder above. Pure function -- no hooks, no React, safe to call from a saga, a plain render
 * method, or a unit test.
 */
export const resolveCropColor = ({
    override,
    chemistry,
    purpose,
    crop,
    defaultMapColor,
}: IResolveCropColorInput): string =>
    override ||
    agvColorToHex(chemistry?.color) ||
    agvColorToHex(purpose?.color) ||
    agvColorToHex(crop?.color) ||
    defaultMapColor;
